import PropTypes from 'prop-types';
import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { toast } from 'sonner';
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import LockOutlined from '@ant-design/icons/LockOutlined';
import './../../../../assets/styles/TableModalStyles.css';
import PasswordChangeDialog from '../../../../utils/PasswordChangeDialog';

/* ---- Reusable menu row ---- */
function MenuRow({ icon, label, sub, color, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.1,
        cursor: 'pointer',
        transition: 'background 0.12s',
        '&:hover': { bgcolor: color === 'error' ? 'rgba(239,68,68,0.05)' : 'rgba(0,0,0,0.03)' },
        '&:hover .row-icon': {
          bgcolor: color === 'error' ? 'rgba(239,68,68,0.1)' : 'rgba(25,118,210,0.08)',
          color: color === 'error' ? '#ef4444' : 'primary.main'
        }
      }}
    >
      <Box
        className="row-icon"
        sx={{
          width: 34,
          height: 34,
          borderRadius: '9px',
          bgcolor: 'rgba(0,0,0,0.04)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
          color: color === 'error' ? '#ef4444' : 'text.secondary',
          transition: 'all 0.12s',
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: color === 'error' ? '#ef4444' : 'text.primary',
            lineHeight: 1.2
          }}
        >
          {label}
        </Typography>
        {sub && <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.2 }}>{sub}</Typography>}
      </Box>
      {color !== 'error' && <Typography sx={{ fontSize: 14, color: 'text.disabled', lineHeight: 1 }}>›</Typography>}
    </Box>
  );
}

export default function ProfileTab() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const apiurl = localStorage.getItem('apiurl');
  const dbTokenData = localStorage.getItem('dbTokenData');
  const authHeaders = { headers: { Authorization: `Bearer ${dbTokenData}`, 'Content-Type': 'application/json' } };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
    window.location.reload();
  };

  const handleProfile = () => {
    window.open('/employeeprofile', '_blank');
  };

  return (
    <>
      <Box sx={{ py: 0.5 }}>
        <Typography
          sx={{
            px: 2,
            pt: 1.2,
            pb: 0.4,
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: 'text.disabled'
          }}
        >
          Account
        </Typography>

        <MenuRow icon={<UserOutlined />} label="View Profile" sub="View and Update your profile" onClick={handleProfile} />
        <MenuRow icon={<LockOutlined />} label="Change Password" sub="Update your credentials" onClick={() => setOpen(true)} />

        <Box sx={{ height: '1px', bgcolor: 'divider', mx: 2, my: 0.5 }} />

        <MenuRow icon={<LogoutOutlined />} label="Logout" color="error" onClick={handleLogout} />
      </Box>

      {/* Change Password Dialog */}
      <PasswordChangeDialog
        open={open}
        onClose={() => setOpen(false)}
        loading={false}
        onSubmit={async ({ currentPassword, newPassword }) => {
          try {
            const empId = localStorage.getItem('useremail');
            const response = await axios.post(
              `${apiurl}login/changepwd`,
              { empId, oldPassword: currentPassword, newPassword },
              authHeaders
            );

            if (response?.data?.cd === 1) {
              toast.error(response.data.description);
              return;
            }

            toast.success(response.data.description);
            handleLogout();
          } catch (error) {
            console.error(error);
            toast.error('Something went wrong. Please try again.');
          }
        }}
      />
    </>
  );
}

ProfileTab.propTypes = { handleLogout: PropTypes.func };
